import { constants } from "@reactivated/constants";

import { Meters, isoKilometers, isoMiles } from "../models/nominals";
import {
    kilometersToMeters,
    metersToKilometers,
    metersToMiles,
    milesToMeters,
} from "./maps";
import { getPageSetting } from "./settings";

const DistanceUnit = constants["tsicommon.types.DistanceUnit"];
type DistanceUnitType = (typeof DistanceUnit)[keyof typeof DistanceUnit];

/**
 * Determines the distance unit based on the site's retail locator settings.
 * Configurable per-site in Django admin.
 */
export const getDistanceUnit = (): DistanceUnitType => {
    const unit = getPageSetting("retail_locator_distance_unit");
    return unit === DistanceUnit.KILOMETERS
        ? DistanceUnit.KILOMETERS
        : DistanceUnit.MILES;
};

/**
 * Distance options available in the filter dropdown, per unit.
 */
export const DISTANCE_OPTIONS: Readonly<
    Record<DistanceUnitType, readonly number[]>
> = {
    [DistanceUnit.MILES]: [10, 20, 30, 40, 50, 75, 100],
    [DistanceUnit.KILOMETERS]: [17, 33, 49, 65, 81, 121, 161],
};

/**
 * Converts meters to the site's configured display unit (miles or kilometers).
 * Returns the numeric value for display purposes.
 */
export const metersToDisplayUnit = (meters: Meters): number => {
    const unit = getDistanceUnit();
    if (unit === DistanceUnit.KILOMETERS) {
        return isoKilometers.unwrap(metersToKilometers(meters));
    }
    return isoMiles.unwrap(metersToMiles(meters));
};

/**
 * Converts a numeric distance value in the site's configured display unit
 * to meters for internal storage.
 */
export const displayUnitToMeters = (value: number): Meters => {
    const unit = getDistanceUnit();
    if (unit === DistanceUnit.KILOMETERS) {
        return kilometersToMeters(isoKilometers.wrap(value));
    }
    return milesToMeters(isoMiles.wrap(value));
};

// Re-export for use in components
export { DistanceUnit };
